import { MetaWrapper } from '@components/Meta'
import { SiteFooterBlockFragment } from '@graphql/fragments/__generated__/SiteFooterBlock.generated'
import { useFlatPageQuery } from '@graphql/queries/__generated__/flatpage.generated'
import { useSiteQuery } from '@graphql/queries/__generated__/site.generated'
import Container from '@mui/material/Container'
import React, { useEffect, useMemo } from 'react'
import { Route, Switch, useLocation } from 'react-router'
import useGlobalLoader from '../../hooks/useGlobalLoader'
import { FEEDBACK_URL, TRACK_ORDER_URL } from '../../urls'
import { maybe } from '../../utils/misc'
import Navigation from './components/Navigation'
import Page from './components/Page'
import FeedbackView from './Feedback/FeedbackView'
import TrackOrderView from './TrackOrder/TrackOrderView'
import MaybeRedirect from '@components/MaybeRedirect'

interface IViewProps {}

const View: React.FC<IViewProps> = () => {
  const { setLoading } = useGlobalLoader()
  const { pathname, search } = useLocation()
  const siteQueryResult = useSiteQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-first',
  })
  const pageQueryResult = useFlatPageQuery({
    variables: {
      url: pathname,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  useEffect(() => {
    setLoading(pageQueryResult.loading && !pageQueryResult.data)
  }, [pageQueryResult])
  const blocks = useMemo<SiteFooterBlockFragment[]>(
    () => maybe<SiteFooterBlockFragment[]>(() => siteQueryResult.data.siteSettings.site.footerBlocks, []),
    [siteQueryResult]
  )
  const block = blocks.find(block => block.links.find(link => pathname.includes(link.linkAddress)))
  if (siteQueryResult.loading && !block) return null
  if (!block) return <MaybeRedirect pathname={pathname} search={search} />
  return (
    <div id='content'>
      <Container maxWidth='xl' className='py-24' disableGutters>
        <div className='flex flex-col sm:flex-row px-10 lg:px-50'>
          <div className='w-full sm:w-200 lg:w-300 mr-20 flex-shrink-0 text-capitalize text-base'>
            <Navigation
              links={block.links.map(link => ({
                url: link.linkAddress,
                title: link.linkText,
              }))}
              title={block.name}
            />
          </div>
          <div className='flex-1 overflow-auto'>
            <Switch>
              <Route path={TRACK_ORDER_URL}>
                <TrackOrderView />
              </Route>
              <Route path={FEEDBACK_URL}>
                <FeedbackView />
              </Route>
              {block.links.map(link => (
                <Route
                  key={link.id}
                  path={link.linkAddress}
                  component={() => (
                    <MetaWrapper titleDetails={link.linkText}>
                      <Page page={pageQueryResult.data?.flatpage} />
                    </MetaWrapper>
                  )}
                />
              ))}
            </Switch>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default View
